import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface PromptCandidate {
  id: string;
  priority: number;
  wantsOpen: boolean;
}

interface GlobalPromptArbiterValue {
  registerCandidate: (candidate: PromptCandidate) => void;
  unregisterCandidate: (id: string) => void;
  activePromptId: string | null;
}

const GlobalPromptArbiterContext = createContext<GlobalPromptArbiterValue | null>(null);

const pickActivePrompt = (candidates: Map<string, PromptCandidate>): string | null => {
  let activeId: string | null = null;
  let highestPriority = Number.NEGATIVE_INFINITY;
  Array.from(candidates.values()).forEach((candidate) => {
    if (!candidate.wantsOpen) return;
    if (candidate.priority > highestPriority) {
      highestPriority = candidate.priority;
      activeId = candidate.id;
    }
  });
  return activeId;
};

export const GlobalPromptArbiterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [candidates, setCandidates] = useState<Map<string, PromptCandidate>>(new Map());

  const registerCandidate = useCallback((candidate: PromptCandidate) => {
    setCandidates((prev) => {
      const current = prev.get(candidate.id);
      if (
        current &&
        current.priority === candidate.priority &&
        current.wantsOpen === candidate.wantsOpen
      ) {
        return prev;
      }
      const next = new Map(prev);
      next.set(candidate.id, candidate);
      return next;
    });
  }, []);

  const unregisterCandidate = useCallback((id: string) => {
    setCandidates((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const activePromptId = useMemo(() => pickActivePrompt(candidates), [candidates]);

  const value = useMemo<GlobalPromptArbiterValue>(
    () => ({
      registerCandidate,
      unregisterCandidate,
      activePromptId,
    }),
    [registerCandidate, unregisterCandidate, activePromptId]
  );

  return (
    <GlobalPromptArbiterContext.Provider value={value}>
      {children}
    </GlobalPromptArbiterContext.Provider>
  );
};

export const useGlobalPromptArbiter = (): GlobalPromptArbiterValue => {
  const ctx = useContext(GlobalPromptArbiterContext);
  if (!ctx) {
    throw new Error('useGlobalPromptArbiter must be used inside GlobalPromptArbiterProvider.');
  }
  return ctx;
};
